import enabledCurrencies from '@data/enabled-currencies';
import { ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import styled from 'styled-components';
import SEO from '@/components/seo';
import {
  CardLink,
  CardTitle,
  CtaPanel,
  Eyebrow,
  FeatureCard,
  HeroBackground,
  HeroTitle,
  Lead,
  Muted,
  Reveal,
  Section,
  SectionContainer,
} from '@/components/ui';
import config from '@/config';
import i18n from '@/i18n.json';
import { loadMessages } from '@/lib/messages';
import { byName, groupByNetwork } from '@/lib/networks';
import { up } from '@/themes';
import { shareImage } from '@/utils/share-image';

const Hero = styled.section`
  isolation: isolate;
  overflow: hidden;
  padding: 64px 0 32px;
  position: relative;

  ${up.sm} {
    padding: 96px 0 48px;
  }
`;

const Title = styled(HeroTitle)`
  margin-top: 16px;
  max-width: 768px;
`;

const Subtitle = styled(Lead)`
  margin-top: 24px;
  max-width: 672px;
`;

const Grid = styled.ul`
  display: grid;
  gap: 16px;
  list-style: none;
  margin: 0;
  padding: 0;

  ${up.sm} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${up.lg} {
    gap: 24px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const NetworkCard = styled(FeatureCard)`
  align-items: center;
  flex-direction: row;
  gap: 16px;
`;

const LogoTile = styled.span`
  align-items: center;
  background-color: color-mix(in oklab, var(--primary) 10%, transparent);
  border-radius: var(--radius-xl);
  display: inline-flex;
  flex-shrink: 0;
  height: 56px;
  justify-content: center;
  width: 56px;

  img {
    height: 32px;
    object-fit: contain;
    width: 32px;
  }
`;

const Details = styled.div`
  flex: 1;
  min-width: 0;
`;

const Arrow = styled(ArrowRight)`
  color: var(--muted-foreground);
  flex-shrink: 0;
  transition: transform 300ms ease, color 300ms ease;

  ${NetworkCard}:hover & {
    color: var(--primary);
    transform: translateX(4px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    ${NetworkCard}:hover & {
      transform: none;
    }
  }
`;

const NetworksIndex = ({ networks, canonicalUrl }) => {
  const t = useTranslations('networksIndex');
  const locale = useLocale();

  return (
    <>
      <SEO
        title={t('seoTitle')}
        description={t('seoDescription')}
        url={canonicalUrl}
        hreflangPath="/networks"
        imageUrl={shareImage.networks(locale)}
        imageAlt={t('title')}
      />

      <Hero>
        <HeroBackground />
        <SectionContainer>
          <Eyebrow>{t('eyebrow')}</Eyebrow>
          <Title>{t('title')}</Title>
          <Subtitle $large>{t('subtitle')}</Subtitle>
        </SectionContainer>
      </Hero>

      <Section id="networks" $spacing="compact">
        <Grid>
          {networks.map((network, index) => (
            <Reveal as="li" key={network.servicePath} delay={(index % 3) * 80}>
              <NetworkCard $interactive>
                <LogoTile>
                  {/* biome-ignore lint/performance/noImgElement: small pre-sized AVIF icon from the CDN */}
                  <img
                    src={`${config.assetsUrl}/networks/${network.servicePath}.avif`}
                    alt=""
                    width={32}
                    height={32}
                    loading="lazy"
                  />
                </LogoTile>
                <Details>
                  <CardTitle as="h2">
                    <CardLink as={Link} href={`/networks/${network.slug}`}>
                      {network.name}
                    </CardLink>
                  </CardTitle>
                  <Muted>{t('currencyCount', { count: network.currencyCount })}</Muted>
                </Details>
                <Arrow size={20} strokeWidth={1.75} aria-hidden="true" />
              </NetworkCard>
            </Reveal>
          ))}
        </Grid>
      </Section>

      <Section id="browse">
        <CtaPanel
          title={t('ctaTitle')}
          description={t('ctaSubtitle')}
          actions={[{ label: t('ctaButton'), href: '/buy' }]}
        />
      </Section>
    </>
  );
};

export async function getStaticProps({ locale }) {
  const networks = groupByNetwork(enabledCurrencies)
    .map(({ name, servicePath, slug, currencies }) => ({
      name,
      servicePath,
      slug,
      currencyCount: currencies.length,
    }))
    .sort((a, b) => b.currencyCount - a.currencyCount || byName(a, b));

  const canonicalUrl = `${config.rootUrl}${locale === i18n.defaultLocale ? '' : `/${locale}`}/networks`;

  return {
    props: {
      networks,
      canonicalUrl,
      messages: loadMessages(locale, { include: ['networksIndex'] }),
    },
  };
}

export default NetworksIndex;
